// library/CreateCard — "What do you want to make?" at the head of the Library
// (redesign C-01), pure.
//
// The author describes a material, optionally picks what kind it is, and
// "Create" hands the request to the assistant: a new conversation opens in the
// chat panel and the request is its first message (the host's `onCreate`).
// The paperclip adds sources (the host's file picker). The kind chips only
// shape the request's wording; the assistant decides how to build it.
//
// Local state is view-ephemeral only: the un-sent text and the picked kind
// (neither survives a reload; both clear once sent).

export { CreateCard };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Icon, type IconName } from "../../components/Icon/Icon.js";
import { IconButton } from "../../components/IconButton/IconButton.js";

type CreateKind = "course" | "lesson" | "quiz" | "document";
const CREATE_KINDS: readonly CreateKind[] = [
    "course",
    "lesson",
    "quiz",
    "document",
];
const KIND_ICON: Record<CreateKind, IconName> = {
    // C-01's chip icons.
    course: "course",
    lesson: "presentation",
    quiz: "mcq",
    document: "document",
};

type CreateCardProps = {
    // Send the request to the assistant (non-empty, trimmed).
    readonly onCreate: (request: string) => void;
    // Add sources (the file picker); absent ⇒ no paperclip.
    readonly onAddSource?: () => void;
};

const CreateCard = ({ onCreate, onAddSource }: CreateCardProps): ReactNode => {
    const { t } = useLingui();
    const [text, setText] = useState("");
    const [kind, setKind] = useState<CreateKind | null>(null);
    const kindLabel = (k: CreateKind): string => {
        switch (k) {
            case "course":
                return t`Course`;
            case "lesson":
                return t`Lesson`;
            case "quiz":
                return t`Quiz`;
            case "document":
                return t`Document`;
            default:
                return exhaustSwitchCase(k);
        }
    };
    // The request as the assistant receives it: the description, led by the
    // picked kind when there is one.
    const request = (): string => {
        const said = text.trim();
        switch (kind) {
            case null:
                return said;
            case "course":
                return said === ""
                    ? t`Create a course.`
                    : t`Create a course: ${said}`;
            case "lesson":
                return said === ""
                    ? t`Create a lesson.`
                    : t`Create a lesson: ${said}`;
            case "quiz":
                return said === ""
                    ? t`Create a quiz.`
                    : t`Create a quiz: ${said}`;
            case "document":
                return said === ""
                    ? t`Create a document.`
                    : t`Create a document: ${said}`;
            default:
                return exhaustSwitchCase(kind);
        }
    };
    const ready = request() !== "";
    const create = () => {
        if (!ready) return;
        onCreate(request());
        setText("");
        setKind(null);
    };
    return (
        <form
            className="bsx-create"
            aria-label={t`Create a material`}
            onSubmit={(e) => {
                e.preventDefault();
                create();
            }}
        >
            <textarea
                className="bsx-create-input"
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (
                        e.key === "Enter" &&
                        !e.shiftKey &&
                        !e.nativeEvent.isComposing
                    ) {
                        e.preventDefault();
                        create();
                    }
                }}
                placeholder={t`What do you want to make? Describe it, or drop in sources – they're saved in the knowledge bank.`}
                aria-label={t`What do you want to make?`}
            />
            <div className="bsx-create-bar">
                {onAddSource !== undefined ? (
                    <>
                        <IconButton
                            label={t`Add sources`}
                            variant="secondary"
                            size="md"
                            className="bsx-create-attach"
                            onClick={onAddSource}
                        >
                            <Icon name="paperclip" size="sm" />
                        </IconButton>
                        <span className="bsx-create-sep" aria-hidden="true" />
                    </>
                ) : null}
                {/* biome-ignore lint/a11y/useSemanticElements: a labelled cluster of toggle buttons; <fieldset> would add a legend the design has no room for. */}
                <div
                    className="bsx-create-kinds"
                    role="group"
                    aria-label={t`Kind of material`}
                >
                    {CREATE_KINDS.map((k) => (
                        <button
                            key={k}
                            type="button"
                            className="bsx-create-kind"
                            aria-pressed={kind === k}
                            onClick={() => setKind(kind === k ? null : k)}
                        >
                            <Icon name={KIND_ICON[k]} size="sm" />
                            {kindLabel(k)}
                        </button>
                    ))}
                </div>
                <button
                    type="submit"
                    className="bsx-create-go"
                    disabled={!ready}
                >
                    {t`Create`}
                </button>
            </div>
        </form>
    );
};
